import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import {
  useApiClientsOrNull,
  useRecentTemplateTouch,
  SegmentedControlAtom,
  ComposerSpherePickerOrganism,
  ContextPreviewPanelMolecule,
  executeContextDefinition,
  AgentThreadOrganism,
  DistributionStatusMolecule,
  ListInChannelDialogOrganism,
  lookupChannelItems,
} from 'design-system/my-lib';
import type {
  SphereSearchResult,
  ComposerAgentContextConfirmation,
  ChannelItemLookupEntry,
} from 'design-system/my-lib';
import type { ContextDefinition, ContextTreeExecutionResult } from 'types/context';
import type { RootState } from '@center-pwa/store/store';
import ContextEditorPage, {
  type ContextEditorTopbarExtrasContext,
  type ContextEditorGhostProposal,
} from '../ContextEditorPage/ContextEditorPage';
import './StudioContextEditorPage.scss';

const COMPOSER_AGENT_ENDPOINT = process.env.REACT_APP_COMPOSER_AGENT_ENDPOINT;

/** Per the transcribed W12 IA, the Distribution tab is absent (never a disabled placeholder) for
 * an unsaved draft or a built-in system context — neither has anything to distribute. */
type SidePanelTab = 'inspect' | 'agent' | 'distribution';

/** Preview limit/depth are the route's own defaults exposed as editor state, not ops knobs. */
const PREVIEW_LIMIT = 50;
const PREVIEW_DEPTH = 4;

/** Session-persisted so the picked preview sphere survives a Save/navigate round trip within
 * the same tab (SC4) without leaking across tabs/sessions. */
const PREVIEW_SPHERE_SESSION_KEY = 'studio.context.previewSphere';

function readPersistedPreviewSphere(): SphereSearchResult | null {
  try {
    const raw = globalThis.window.sessionStorage.getItem(PREVIEW_SPHERE_SESSION_KEY);
    return raw ? (JSON.parse(raw) as SphereSearchResult) : null;
  } catch {
    return null;
  }
}

/**
 * `/studio/context/new` and `/studio/context/:systemName` (EAINT-862) — the Studio host for
 * `ContextEditorPage`. Re-parents the editor unchanged (`inline`), owns post-save navigation,
 * the recent-templates touch, the `?node=` deep-link level selection, and wraps the existing
 * inspector `<aside>` in a tab strip (Inspect/Agent — W12). Phase 2 adds the host-owned sphere
 * picker + Preview (`executeContextDefinition`); Phase 3 adds the Agent tab's ghost walk.
 */
const StudioContextEditorPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { systemName } = useParams<{ systemName?: string }>();
  const [searchParams] = useSearchParams();
  const apiClients = useApiClientsOrNull();
  const touchRecentTemplate = useRecentTemplateTouch(apiClients);
  const logAnalyticsEvent = useAnalytics();

  const userSphere = useSelector((s: RootState) => s.userProfile?.userSphere);

  // `?node=<levelId>` — the Studio Desk finding-row / recent-template deep-link contract
  // (EAINT-865), deferred to this spec.
  const initialSelectedLevelId = searchParams.get('node') ?? undefined;

  const [activeSidePanelTab, setActiveSidePanelTab] = useState<SidePanelTab>('inspect');

  // ── Agent tab (EAINT-862 Phase 3) ────────────────────────────────────────────
  const [ghost, setGhost] = useState<ContextEditorGhostProposal | null>(null);

  // The org-bearing session token first (same source as every ApiClients call — the
  // interceptor's tokenProvider); the bare Descope session token only as the fallback the interceptor itself uses.
  const agentGetToken = useCallback(
    async () => {
      // The org-bearing session token from the ApiClients tokenProvider - the same source every
      // API call uses. There is no second token source: a bare identity token would carry no
      // organization claim and 401 org-scoped routes when the agent service forwards it.
      const token = await apiClients?.getAuthToken?.();
      if (!token) throw new Error('No session token available for the agent client');
      return token;
  },
    [apiClients]
  );

  const handleContextProposal = useCallback((proposal: ComposerAgentContextConfirmation) => {
    setGhost({
      name: proposal.name,
      description: proposal.description,
      applicability: proposal.applicability,
      definition: proposal.definition as unknown as ContextDefinition,
      useCaseTags: proposal.useCaseTags,
    });
  }, []);

  const handleAcceptGhost = useCallback(() => {
    logAnalyticsEvent('context_agent_proposal_accepted', undefined, {
      levels: ghost?.definition.levels?.length ?? 0,
    });
    setGhost(null);
  }, [ghost, logAnalyticsEvent]);

  const handleDismissGhost = useCallback(() => {
    logAnalyticsEvent('context_agent_proposal_dismissed', undefined, {
      levels: ghost?.definition.levels?.length ?? 0,
    });
    setGhost(null);
  }, [ghost, logAnalyticsEvent]);

  // ── Preview (EAINT-862 Phase 2) ──────────────────────────────────────────────
  const [pickerOpen, setPickerOpen] = useState(false);
  const [previewSphere, setPreviewSphere] = useState<SphereSearchResult | null>(
    readPersistedPreviewSphere
  );
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<ContextTreeExecutionResult | null>(null);

  // Defaults to the caller's org sphere until they pick one explicitly (SC4). Read straight
  // off the redux profile — never a per-sphere permission call, matching the picker's own
  // "id + label only" result shape.
  const effectivePreviewSphere: SphereSearchResult | null = useMemo(
    () => previewSphere ?? (userSphere ? { id: userSphere.id, label: userSphere.label } : null),
    [previewSphere, userSphere]
  );

  const handlePickSphere = useCallback((sphere: SphereSearchResult) => {
    setPreviewSphere(sphere);
    try {
      globalThis.window.sessionStorage.setItem(PREVIEW_SPHERE_SESSION_KEY, JSON.stringify(sphere));
    } catch {
      // sessionStorage unavailable (private mode, quota) — the pick still works for this render
    }
    setPickerOpen(false);
  }, []);

  const handleRunPreview = useCallback(
    async (definition: ContextDefinition) => {
      if (!apiClients?.graphClient || !effectivePreviewSphere) return;
      setPreviewOpen(true);
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const result = await executeContextDefinition(apiClients.graphClient, {
          definition,
          rootSphereId: effectivePreviewSphere.id,
          responseMode: 'tree',
          limit: PREVIEW_LIMIT,
          depth: PREVIEW_DEPTH,
        });
        setPreviewResult(result);
        logAnalyticsEvent('context_previewed', undefined, {
          levels: definition.levels.length,
          totalLoaded: result.stats.totalLoaded,
          executionTimeMs: result.stats.executionTimeMs,
          isNew: !systemName,
        });
      } catch (err) {
        setPreviewResult(null);
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 403) {
          setPreviewError(t('context.editor.preview.errorForbidden'));
        } else if (status === 400) {
          setPreviewError(t('context.editor.preview.errorBadRequest'));
        } else {
          setPreviewError(t('context.editor.preview.errorGeneric'));
        }
      } finally {
        setPreviewLoading(false);
      }
    },
    [apiClients, effectivePreviewSphere, systemName, t, logAnalyticsEvent]
  );

  const renderTopbarExtras = useCallback(
    ({ canPreview, buildDefinition }: ContextEditorTopbarExtrasContext) => (
      <div className="studio-context-editor-page__topbar-extras">
        <button
          type="button"
          className="studio-context-editor-page__sphere-picker-btn"
          onClick={() => setPickerOpen(true)}
          aria-label={t('context.editor.actions.previewSpherePicker')}
          data-testid="studio-context-preview-sphere-btn"
        >
          <i className="pi pi-map-marker" aria-hidden="true" />
          {effectivePreviewSphere?.label ?? t('context.editor.actions.previewSpherePicker')}
        </button>
        <button
          type="button"
          className="context-editor-page__preview-btn"
          disabled={!canPreview || !effectivePreviewSphere}
          onClick={() => handleRunPreview(buildDefinition())}
          aria-label={t('context.editor.actions.preview')}
          data-testid="studio-context-preview-btn"
        >
          <i className="pi pi-play" aria-hidden="true" />
          {t('context.editor.actions.preview')}
        </button>
      </div>
    ),
    [effectivePreviewSphere, handleRunPreview, t]
  );

  // ── Distribution (EAINT-862 Phase 4) ─────────────────────────────────────────
  const [distributionChannels, setDistributionChannels] = useState<ChannelItemLookupEntry[] | null>(null);
  const [distributionDegraded, setDistributionDegraded] = useState(false);
  const [listInChannelOpen, setListInChannelOpen] = useState(false);

  const fetchDistribution = useCallback(() => {
    if (!apiClients?.schemaClient || !systemName) return;
    setDistributionDegraded(false);
    lookupChannelItems(apiClients.schemaClient, 'context', systemName)
      .then(setDistributionChannels)
      .catch(() => {
        setDistributionChannels(null);
        setDistributionDegraded(true);
      });
    // Depends on the client reference, not the `apiClients` object itself — the context value
    // is normally stable, but keying on the whole object risks a refetch loop if a caller (or a
    // test double) ever hands back a fresh object per render.
  }, [apiClients?.schemaClient, systemName]);

  useEffect(() => {
    setDistributionChannels(null);
    setDistributionDegraded(false);
    fetchDistribution();
  }, [fetchDistribution]);

  const handleBackToGallery = useCallback(() => {
    navigate('/studio/context');
  }, [navigate]);

  // Recent-templates touch fires once per mount (an existing context being opened) — a brand
  // new draft has no template identity to touch until it's saved, where `handleSaved` below
  // touches it instead.
  const touchedOnMountRef = useRef(false);
  useEffect(() => {
    if (!systemName || touchedOnMountRef.current) return;
    touchedOnMountRef.current = true;
    touchRecentTemplate(systemName);
  }, [systemName, touchRecentTemplate]);

  const handleSaved = useCallback(
    (savedSystemName: string, isNew: boolean) => {
      touchRecentTemplate(savedSystemName);
      if (isNew || savedSystemName !== systemName) {
        navigate(`/studio/context/${savedSystemName}`, { replace: true });
      }
    },
    [navigate, systemName, touchRecentTemplate]
  );

  const renderSidePanel = useCallback(
    (inspector: ReactNode, ctx: { isBuiltIn: boolean; isNew: boolean }) => {
      // Unsaved drafts and built-in system contexts have nothing to distribute — the tab is
      // absent, not disabled (W12 IA).
      const showDistribution = !ctx.isNew && !ctx.isBuiltIn;
      const tabOptions = [
        { value: 'inspect' as const, label: t('studio.context.editor.sidePanel.inspect') },
        { value: 'agent' as const, label: t('studio.context.editor.sidePanel.agent') },
        ...(showDistribution
          ? [{ value: 'distribution' as const, label: t('studio.context.editor.sidePanel.distribution') }]
          : []),
      ];
      const activeTab = tabOptions.some((opt) => opt.value === activeSidePanelTab)
        ? activeSidePanelTab
        : 'inspect';

      return (
        <div className="studio-context-editor-page__side-panel" data-testid="studio-context-side-panel">
          <SegmentedControlAtom
            options={tabOptions}
            value={activeTab}
            onChange={(value) => setActiveSidePanelTab(value as SidePanelTab)}
            mode="single"
            variant="strip"
            ariaLabel={t('studio.context.editor.sidePanel.ariaLabel')}
            data-testid="studio-context-side-panel-tabs"
          />
          <div className="studio-context-editor-page__side-panel-body">
            {activeTab === 'agent' ? (
              <AgentThreadOrganism
                endpoint={COMPOSER_AGENT_ENDPOINT}
                getToken={agentGetToken}
                target={{ kind: 'context', id: systemName ?? 'new' }}
                onContextProposal={handleContextProposal}
              />
            ) : activeTab === 'distribution' ? (
              <>
                <DistributionStatusMolecule
                  channels={distributionChannels}
                  degraded={distributionDegraded}
                  onNavigateToChannel={(channelId) =>
                    navigate(`/marketplace/channels/${encodeURIComponent(channelId)}`)
                  }
                  onListInChannel={() => setListInChannelOpen(true)}
                />
                <ListInChannelDialogOrganism
                  visible={listInChannelOpen}
                  onHide={() => setListInChannelOpen(false)}
                  itemType="context"
                  itemSystemName={systemName ?? ''}
                  alreadyListedChannelIds={(distributionChannels ?? []).map((c) => c.channel_id)}
                  onListed={fetchDistribution}
                />
              </>
            ) : (
              inspector
            )}
          </div>
        </div>
      );
    },
    [
      activeSidePanelTab,
      t,
      agentGetToken,
      systemName,
      handleContextProposal,
      distributionChannels,
      distributionDegraded,
      listInChannelOpen,
      navigate,
      fetchDistribution,
    ]
  );

  return (
    <div className="studio-context-editor-page" data-testid="studio-context-editor-page">
      <ContextEditorPage
        inline
        inlineSystemName={systemName}
        onBack={handleBackToGallery}
        onClose={handleBackToGallery}
        onSaved={handleSaved}
        renderSidePanel={renderSidePanel}
        renderTopbarExtras={renderTopbarExtras}
        initialSelectedLevelId={initialSelectedLevelId}
        ghost={ghost ?? undefined}
        onAcceptGhost={handleAcceptGhost}
        onDismissGhost={handleDismissGhost}
      />
      <ComposerSpherePickerOrganism
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        titleKey="context.editor.actions.previewSpherePicker"
        onPick={handlePickSphere}
      />
      <ContextPreviewPanelMolecule
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        loading={previewLoading}
        error={previewError}
        result={previewResult}
      />
    </div>
  );
};

export default StudioContextEditorPage;
